import React, { useEffect, useRef, useState } from "react";
import { Button, Menu, MenuItem, Typography } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import styles from "../../../styles/misreports/misreport.module.css";
import BarChart from "../../../Components/GraphNCharts/BarChart";
import CustomTable from "../../../Components/MisReport/Table";
import companyLogo from "../../../assets/images/nirantara_logo_new.svg";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import html2pdf from "html2pdf.js";
import { useUser } from "../../../context/AuthContext";
import { get } from "../../../utils/api";
import { truncateNumber as truncateNumberRaw } from "../../../utils/common";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { useParams, useLocation } from "react-router-dom";

/**
 * Word ignores most CSS on data-uri images but honors width/height.
 * Chart.js canvases are often 2–3× layout size — downscale so the .doc fits the page.
 */
function chartCanvasForWordExport(sourceCanvas, maxWidthPx = 640) {
  if (!(sourceCanvas instanceof HTMLCanvasElement)) return null;
  const sw = sourceCanvas.width;
  const sh = sourceCanvas.height;
  if (!sw || !sh) {
    try {
      return {
        dataUrl: sourceCanvas.toDataURL("image/png"),
        width: Math.max(sw, 1),
        height: Math.max(sh, 1),
      };
    } catch {
      return null;
    }
  }
  const scale = Math.min(1, maxWidthPx / sw);
  const dw = Math.max(1, Math.round(sw * scale));
  const dh = Math.max(1, Math.round(sh * scale));
  if (scale >= 1) {
    try {
      return {
        dataUrl: sourceCanvas.toDataURL("image/png"),
        width: sw,
        height: sh,
      };
    } catch {
      return null;
    }
  }
  const out = document.createElement("canvas");
  out.width = dw;
  out.height = dh;
  const ctx = out.getContext("2d");
  if (!ctx) {
    try {
      return {
        dataUrl: sourceCanvas.toDataURL("image/png"),
        width: sw,
        height: sh,
      };
    } catch {
      return null;
    }
  }
  ctx.drawImage(sourceCanvas, 0, 0, sw, sh, 0, 0, dw, dh);
  try {
    return { dataUrl: out.toDataURL("image/png"), width: dw, height: dh };
  } catch {
    return null;
  }
}

const MisReportCard = () => {
  const truncateNumber = (value) => truncateNumberRaw(value, 4);
  const reportRef = useRef();
  const { user } = useUser();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const location = useLocation();  // State to track loading
  const searchParams = new URLSearchParams(location.search);
  const reportYear = searchParams.get("year");

  const [data, setData] = useState({});
  const [scope1BarChartData, setScope1BarChartData] = useState([]);
  const [scope2BarChartData, setScope2BarChartData] = useState([]);
  const [scope3BarChartData, setScope3BarChartData] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [summary, setSummary] = useState({
    scope1: "",
    scope2: "",
    scope3: "",
  });

  const [base64Logo, setBase64Logo] = useState("");

  const convertToBase64 = async (url) => {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`);
      const blob = await res.blob();

      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.error("Image conversion failed:", err);
      return "";
    }
  };

  useEffect(() => {
    // Convert image once when component mounts
    convertToBase64(user?.company?.companyImage)
      .then((result) => {
        setBase64Logo(result);
      })
      .catch(console.error);
  }, []);


  const getData = async () => {
    if (id) {
      try {
        setLoading(true);

        const response = await get(`/mis-report/data?id=${id}`);
        setData(response?.data?.data);
      } catch (error) {
        console.log("error", error);
      } finally {
        setLoading(false); // Stop loading after response (success or error)
      }
    }
  };

  const getGHGFacilityWiseData = async () => {
    try {
      const response = await get(`/mis-report/ghg?id=${id}`);
      setScope1BarChartData(response?.data?.scope1);
      setScope2BarChartData(response?.data?.scope2);
      setScope3BarChartData(response?.data?.scope3);
      setSummary({
        scope1: response?.data?.scope1Summary,
        scope2: response?.data?.scope2Summary,
        scope3: response?.data?.scope3Summary,
      });
    } catch (error) {
      console.log("error", error);
    }
  };


  useEffect(() => {
    getData();
    getGHGFacilityWiseData();
    // waterFacilityWiseData();
  }, []);

  const scope1Headers = [
    "Emission Source",
    "Current Month (tCO2e)",
    "Previous Month (tCO2e)",
    "% Change",
    // "Peer Average (tCO2e)",
  ];

  const energyConsumptionHeaders = [
    "Energy Source",
    "Current Month (MJ)",
    "Previous Month (MJ)",
    "% Change",
    // "Peer Average (MWh)",
  ];

  const waterConsumptionHeaders = [
    "Metric",
    "Current Month (m3)",
    "Previous Month (m3)",
    "% Change",
    // "Peer Average (m3)",
  ];

  const wasteManagementHeaders = [
    "Metric",
    "Current Month (tonnes)",
    "Previous Month (tonnes)",
    "% Change",
    // "Peer Average (tonnes)",
  ];

  const logisticSummaryHeaders = [
    "Metric",
    "Current Month",
    "Previous Month",
    "% Change",
  ];

  const emissionIntensityHeaders = [
    "Metric",
    "Current Month",
    "Previous Month",
    "% Change",
    // "Peer Average",
  ];

  const socialMetricsDiversityHeaders = [
    "Metric",
    "Current Month (%)",
    "Previous Month (%)",
    "% Change",
    // "Peer Average (%)",
  ];

  const socialMetricsHeaders = [
    "Metric",
    "Current Month",
    "Previous Month",
    "% Change",
    // "Peer Average",
  ];

  const govtMetricsHeaders = [
    "Metric",
    "Current Quarter",
    "Previous Quarter",
    "% Change",
    // "Peer Average",
  ];

  const scope1Rows = [
    {
      "Emission Source": "Stationary Combustion",
      "Current Month (tCO2e)": truncateNumber(
        data?.scope1Emissions?.stationaryCombustion?.thisMonthTotal ?? 0,
        4
      ),
      "Previous Month (tCO2e)": truncateNumber(
        data?.scope1Emissions?.stationaryCombustion?.previousMonthTotal ?? 0,
        4
      ),
      "% Change": truncateNumber(
        data?.scope1Emissions?.stationaryCombustion?.percentageChange ?? 0,
        4
      ),
      // "Peer Average (tCO2e)": Number(
      //   data?.scope1Emissions?.stationaryCombustion?.peerAverage ?? 0
      // ).toFixed(3),
    },
    {
      "Emission Source": "Mobile Combustion",
      "Current Month (tCO2e)": truncateNumber(
        data?.scope1Emissions?.mobileCombustion?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month (tCO2e)": truncateNumber(
        data?.scope1Emissions?.mobileCombustion?.previousMonthTotal ?? 0,
        2
      ),
      "% Change": truncateNumber(
        data?.scope1Emissions?.mobileCombustion?.percentageChange ?? 0,
        2
      ),
      // "Peer Average (tCO2e)":truncateNumber(
      //   data?.scope1Emissions?.mobileCombustion?.peerAverage ?? 0
      // ),
    },
    {
      "Emission Source": "Fugitive Emission",
      "Current Month (tCO2e)": truncateNumber(
        data?.scope1Emissions?.fugitiveCombustion?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month (tCO2e)": truncateNumber(
        data?.scope1Emissions?.fugitiveCombustion?.previousMonthTotal ?? 0,
        2
      ),
      "% Change": truncateNumber(
        data?.scope1Emissions?.fugitiveCombustion?.percentageChange ?? 0,
        2
      ),
      // "Peer Average (tCO2e)":truncateNumber(
      //   data?.scope1Emissions?.fugitiveCombustion?.peerAverage ?? 0
      // ),
    },
    {
      "Emission Source": "Total Scope 1 Emission",
      "Current Month (tCO2e)": truncateNumber(
        data?.scope1Emissions?.totalScope1Combustion?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month (tCO2e)": truncateNumber(
        data?.scope1Emissions?.totalScope1Combustion?.previousMonthTotal ?? 0,
        2
      ),
      "% Change": truncateNumber(
        data?.scope1Emissions?.totalScope1Combustion?.percentageChange ?? 0,
        2
      ),
      // "Peer Average (tCO2e)":truncateNumber(
      //   data?.scope1Emissions?.totalScope1Combustion?.peerAverage ?? 0
      // ),
    },
  ];

  const scope2Rows = [
    {
      "Emission Source": "Electricity",
      "Current Month (tCO2e)": truncateNumber(
        data?.scope2Emissions?.electricity?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month (tCO2e)": truncateNumber(
        data?.scope2Emissions?.electricity?.previousMonthTotal ?? 0,
        2
      ),
      "% Change": truncateNumber(
        data?.scope2Emissions?.electricity?.percentageChange ?? 0,
        2
      ),
      // "Peer Average (tCO2e)": truncateNumber(
      //   data?.scope2Emissions?.electricity?.peerAverage ?? 0
      // ),
    },
    {
      "Emission Source": "Heating and Cooling",
      "Current Month (tCO2e)": truncateNumber(
        data?.scope2Emissions?.heat?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month (tCO2e)": truncateNumber(
        data?.scope2Emissions?.heat?.previousMonthTotal ?? 0,
        2
      ),
      "% Change": truncateNumber(
        data?.scope2Emissions?.heat?.percentageChange ?? 0,
        2
      ),
      // "Peer Average (tCO2e)": truncateNumber(
      //   data?.scope2Emissions?.heat?.peerAverage ?? 0
      // ),
    },
    {
      "Emission Source": "Steam",
      "Current Month (tCO2e)": truncateNumber(
        data?.scope2Emissions?.steam?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month (tCO2e)": truncateNumber(
        data?.scope2Emissions?.steam?.previousMonthTotal ?? 0,
        2
      ),
      "% Change": truncateNumber(
        data?.scope2Emissions?.steam?.percentageChange ?? 0,
        2
      ),
      // "Peer Average (tCO2e)": truncateNumber(
      //   data?.scope2Emissions?.steam?.peerAverage ?? 0
      // ),
    },
    {
      "Emission Source": "Total Scope 2 Emission",
      "Current Month (tCO2e)": truncateNumber(
        data?.scope2Emissions?.totalEmission?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month (tCO2e)": truncateNumber(
        data?.scope2Emissions?.totalEmission?.previousMonthTotal ?? 0,
        2
      ),
      "% Change": truncateNumber(
        data?.scope2Emissions?.totalEmission?.percentageChange ?? 0,
        2
      ),
      // "Peer Average (tCO2e)": truncateNumber(
      //   data?.scope2Emissions?.totalEmission?.peerAverage ?? 0
      // ),
    },
  ];

  const scope3Rows = [
    {
      "Emission Source": "Purchased Goods & Servcies",
      "Current Month (tCO2e)": truncateNumber(
        data?.scope3Emissions?.purchasedGoods?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month (tCO2e)": truncateNumber(
        data?.scope3Emissions?.purchasedGoods?.previousMonthTotal ?? 0,
        2
      ),
      "% Change": truncateNumber(
        data?.scope3Emissions?.purchasedGoods?.percentageChange ?? 0,
        2
      ),
      // "Peer Average (tCO2e)": truncateNumber(
      //   data?.scope3Emissions?.purchasedGoods?.peerAverage ?? 0
      // ),
    },
    {
      "Emission Source": "Upstream/Downstream Transportation & Distribution",
      "Current Month (tCO2e)": truncateNumber(
        data?.scope3Emissions?.upstreamAndDownStream?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month (tCO2e)": truncateNumber(
        data?.scope3Emissions?.upstreamAndDownStream?.previousMonthTotal ?? 0,
        2
      ),
      "% Change": truncateNumber(
        data?.scope3Emissions?.upstreamAndDownStream?.percentageChange ?? 0,
        2
      ),
      // "Peer Average (tCO2e)": truncateNumber(
      //   data?.scope3Emissions?.upstreamAndDownStream?.peerAverage ?? 0
      // ),
    },
    {
      "Emission Source": "Waste Generated in Operations",
      "Current Month (tCO2e)": truncateNumber(
        data?.scope3Emissions?.wasteGenerated?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month (tCO2e)": truncateNumber(
        data?.scope3Emissions?.wasteGenerated?.previousMonthTotal ?? 0,
        2
      ),
      "% Change": truncateNumber(
        data?.scope3Emissions?.wasteGenerated?.percentageChange ?? 0,
        2
      ),
      // "Peer Average (tCO2e)": truncateNumber(
      //   data?.scope3Emissions?.wasteGenerated?.peerAverage ?? 0
      // ),
    },
    {
      "Emission Source": "Employee Commute",
      "Current Month (tCO2e)": truncateNumber(
        data?.scope3Emissions?.employeeCommute?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month (tCO2e)": truncateNumber(
        data?.scope3Emissions?.employeeCommute?.previousMonthTotal ?? 0,
        2
      ),
      "% Change": truncateNumber(
        data?.scope3Emissions?.employeeCommute?.percentageChange ?? 0,
        2
      ),
      // "Peer Average (tCO2e)": truncateNumber(
      //   data?.scope3Emissions?.employeeCommute?.peerAverage ?? 0
      // ),
    },
    {
      "Emission Source": "Business Travel",
      "Current Month (tCO2e)": truncateNumber(
        data?.scope3Emissions?.businessTravel?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month (tCO2e)": truncateNumber(
        data?.scope3Emissions?.businessTravel?.previousMonthTotal ?? 0,
        2
      ),
      "% Change": truncateNumber(
        data?.scope3Emissions?.businessTravel?.percentageChange ?? 0,
        2
      ),
      // "Peer Average (tCO2e)": truncateNumber(
      //   data?.scope3Emissions?.businessTravel?.peerAverage ?? 0
      // ),
    },
    {
      "Emission Source": "Total Scope 3 Emission",
      "Current Month (tCO2e)": truncateNumber(
        data?.scope3Emissions?.totalEmission?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month (tCO2e)": truncateNumber(
        data?.scope3Emissions?.totalEmission?.previousMonthTotal ?? 0,
        2
      ),
      "% Change": truncateNumber(
        data?.scope3Emissions?.totalEmission?.percentageChange ?? 0,
        2
      ),
      // "Peer Average (tCO2e)": truncateNumber(
      //   data?.scope3Emissions?.totalEmission?.peerAverage ?? 0
      // ),
    },
  ];

  const energyConsumptionRows = [
    {
      "Emission Source": "Electricity",
      "Current Month (tCO2e)": truncateNumber(
        data?.energyConsumption?.electricity?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month (tCO2e)": truncateNumber(
        data?.energyConsumption?.electricity?.previousMonthTotal ?? 0,
        2
      ),
      "% Change": truncateNumber(
        data?.energyConsumption?.electricity?.percentageChange ?? 0,
        2
      ),
      // "Peer Average (tCO2e)": 0,
    },
    {
      "Emission Source": "Heating and Cooling",
      "Current Month (tCO2e)": truncateNumber(
        data?.energyConsumption?.heat?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month (tCO2e)": truncateNumber(
        data?.energyConsumption?.heat?.previousMonthTotal ?? 0,
        2
      ),
      "% Change": truncateNumber(
        data?.energyConsumption?.heat?.percentageChange ?? 0,
        2
      ),
      // "Peer Average (tCO2e)": 0,
    },
    {
      "Emission Source": "Steam",
      "Current Month (tCO2e)": truncateNumber(
        data?.energyConsumption?.steam?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month (tCO2e)": truncateNumber(
        data?.energyConsumption?.steam?.previousMonthTotal ?? 0,
        2
      ),
      "% Change": truncateNumber(
        data?.energyConsumption?.steam?.percentageChange ?? 0,
        2
      ),
      // "Peer Average (tCO2e)": 0,
    },
    {
      "Emission Source": "Total Energy",
      "Current Month (tCO2e)": truncateNumber(
        data?.energyConsumption?.totalEmission?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month (tCO2e)": truncateNumber(
        data?.energyConsumption?.totalEmission?.previousMonthTotal ?? 0,
        2
      ),
      "% Change": truncateNumber(
        data?.energyConsumption?.totalEmission?.percentageChange ?? 0,
        2
      ),
      // "Peer Average (tCO2e)": 0,
    },
  ];

  const waterConsumptionRows = [
    {
      "Emission Source": "Total Water",
      "Current Month (tCO2e)": truncateNumber(
        data?.waterConsumption?.totalWater?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month (tCO2e)": truncateNumber(
        data?.waterConsumption?.totalWater?.previousMonthTotal ?? 0,
        2
      ),
      "% Change": truncateNumber(
        data?.waterConsumption?.totalWater?.percentageChange ?? 0,
        2
      ),
      // "Peer Average (tCO2e)": truncateNumber(
      //   data?.waterConsumption?.totalWater?.peerAverage ?? 0
      // ),
    },
    {
      "Emission Source": "Water Recycled",
      "Current Month (tCO2e)": truncateNumber(
        data?.waterConsumption?.waterRecycled?.thisMonthWaterRecycled ?? 0,
        2
      ),
      "Previous Month (tCO2e)": truncateNumber(
        data?.waterConsumption?.waterRecycled?.previousMonthWaterRecycled ?? 0,
        2
      ),
      "% Change": truncateNumber(
        data?.waterConsumption?.waterRecycled?.percentageChange ?? 0,
        2
      ),
      // "Peer Average (tCO2e)": truncateNumber(
      //   data?.waterConsumption?.waterRecycled?.peerAverage ?? 0
      // ),
    },
  ];

  const wasteManagementRows = [
    {
      "Emission Source": "Total Waste",
      "Current Month (tCO2e)": truncateNumber(
        data?.wasteManagement?.totalWaste?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month (tCO2e)": truncateNumber(
        data?.wasteManagement?.totalWaste?.previousMonthTotal ?? 0,
        2
      ),
      "% Change": truncateNumber(
        data?.wasteManagement?.totalWaste?.percentageChange ?? 0,
        2
      ),
      // "Peer Average (tCO2e)": truncateNumber(
      //   data?.wasteManagement?.totalWaste?.peerAverage ?? 0
      // ),
    },
    {
      "Emission Source": "Hazardous Waste",
      "Current Month (tCO2e)": truncateNumber(
        data?.wasteManagement?.hazardousWaste?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month (tCO2e)": truncateNumber(
        data?.wasteManagement?.hazardousWaste?.previousMonthTotal ?? 0,
        2
      ),
      "% Change": truncateNumber(
        data?.wasteManagement?.hazardousWaste?.percentageChange ?? 0,
        2
      ),
      // "Peer Average (tCO2e)": truncateNumber(
      //   data?.wasteManagement?.hazardousWaste?.peerAverage ?? 0
      // ),
    },
    {
      "Emission Source": "Non-Hazardous Waste",
      "Current Month (tCO2e)": truncateNumber(
        data?.wasteManagement?.nonHazardousWaste?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month (tCO2e)": truncateNumber(
        data?.wasteManagement?.nonHazardousWaste?.previousMonthTotal ?? 0,
        2
      ),
      "% Change": truncateNumber(
        data?.wasteManagement?.nonHazardousWaste?.percentageChange ?? 0,
        2
      ),
      // "Peer Average (tCO2e)": truncateNumber(
      //   data?.wasteManagement?.nonHazardousWaste?.peerAverage ?? 0
      // ),
    },
    {
      "Emission Source": "Waste Diverted",
      "Current Month (tCO2e)": truncateNumber(
        data?.wasteManagement?.wasteDiverted?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month (tCO2e)": truncateNumber(
        data?.wasteManagement?.wasteDiverted?.previousMonthTotal ?? 0,
        2
      ),
      "% Change": truncateNumber(
        data?.wasteManagement?.wasteDiverted?.percentageChange ?? 0,
        2
      ),
      // "Peer Average (tCO2e)": truncateNumber(
      //   data?.wasteManagement?.wasteDiverted?.peerAverage ?? 0
      // ),
    },
  ];

  const upstreamLogisticRows = [
    {
      Metric: "Total Number of Trips",
      "Current Month": truncateNumber(
        data?.upStreamLogisticSummary?.totalTrips?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month": truncateNumber(
        data?.upStreamLogisticSummary?.totalTrips?.previousMonthTotal ?? 0,
        2
      ),
      Target: truncateNumber(
        data?.upStreamLogisticSummary?.totalTrips?.percentageChange ?? 0,
        2
      ),
    },
    {
      Metric: "Total Mass/volume / SKU (stock keeping units) carried",
      "Current Month": truncateNumber(
        data?.upStreamLogisticSummary?.totalMass?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month": truncateNumber(
        data?.upStreamLogisticSummary?.totalMass?.previousMonthTotal ?? 0,
        2
      ),
      Target: truncateNumber(
        data?.upStreamLogisticSummary?.totalMass?.percentageChange ?? 0,
        2
      ),
    },
  ];

  const upstreamEmissionIntensityRows = [
    {
      Metric: "Average Emission Per Trip (tCO2e/Trip)",
      "Current Month": truncateNumber(
        data?.upStreamEmissionIntensity?.averageEmissionPerTrip
          ?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month": truncateNumber(
        data?.upStreamEmissionIntensity?.averageEmissionPerTrip
          ?.previousMonthTotal ?? 0,
        2
      ),
      Target: truncateNumber(
        data?.upStreamEmissionIntensity?.averageEmissionPerTrip
          ?.percentageChange ?? 0,
        2
      ),
      // "Peer Average": 0,
    },
    {
      Metric: "Emission Intensity by Mass/Volume (tCO2e/ton or m3)",
      "Current Month": truncateNumber(
        data?.upStreamEmissionIntensity?.emissionMass?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month": truncateNumber(
        data?.upStreamEmissionIntensity?.emissionMass?.previousMonthTotal ?? 0,
        2
      ),
      Target: truncateNumber(
        data?.upStreamEmissionIntensity?.emissionMass?.percentageChange ?? 0,
        2
      ),
      // "Peer Average": 0,
    },
    {
      Metric: "Emission Intensity by Distance (tCO2e/km)",
      "Current Month": truncateNumber(
        data?.upStreamEmissionIntensity?.emissionDistance?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month": truncateNumber(
        data?.upStreamEmissionIntensity?.emissionDistance?.previousMonthTotal ??
          0,
        2
      ),
      Target: truncateNumber(
        data?.upStreamEmissionIntensity?.emissionDistance?.percentageChange ??
          0,
        2
      ),
      // "Peer Average": 0,
    },
  ];

  const downstreamLogisticRows = [
    {
      Metric: "Total Number of Trips",
      "Current Month": truncateNumber(
        data?.downstreamLogisticSummary?.totalTrips?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month": truncateNumber(
        data?.downstreamLogisticSummary?.totalTrips?.previousMonthTotal ?? 0,
        2
      ),
      Target: truncateNumber(
        data?.downstreamLogisticSummary?.totalTrips?.percentageChange ?? 0,
        2
      ),
    },
    {
      Metric: "Total Mass/volume / SKU (stock keeping units) carried",
      "Current Month": truncateNumber(
        data?.downstreamLogisticSummary?.totalMass?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month": truncateNumber(
        data?.downstreamLogisticSummary?.totalMass?.previousMonthTotal ?? 0,
        2
      ),
      Target: truncateNumber(
        data?.downstreamLogisticSummary?.totalMass?.percentageChange ?? 0,
        2
      ),
    },
  ];

  const downstreamEmissionIntensityRows = [
    {
      Metric: "Average Emission Per Trip (tCO2e/Trip)",
      "Current Month": truncateNumber(
        data?.downStreamEmissionIntensity?.averageEmissionPerTrip
          ?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month": truncateNumber(
        data?.downStreamEmissionIntensity?.averageEmissionPerTrip
          ?.previousMonthTotal ?? 0,
        2
      ),
      Target: truncateNumber(
        data?.downStreamEmissionIntensity?.averageEmissionPerTrip
          ?.percentageChange ?? 0,
        2
      ),
      // "Peer Average": 0,
    },
    {
      Metric: "Emission Intensity by Mass/Volume (tCO2e/ton or m3)",
      "Current Month": truncateNumber(
        data?.downStreamEmissionIntensity?.emissionMass?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month": truncateNumber(
        data?.downStreamEmissionIntensity?.emissionMass?.previousMonthTotal ??
          0,
        2
      ),
      Target: truncateNumber(
        data?.downStreamEmissionIntensity?.emissionMass?.percentageChange ?? 0,
        2
      ),
      // "Peer Average": 0,
    },
    {
      Metric: "Emission Intensity by Distance (tCO2e/km)",
      "Current Month": truncateNumber(
        data?.downStreamEmissionIntensity?.emissionDistance?.thisMonthTotal ??
          0,
        2
      ),
      "Previous Month": truncateNumber(
        data?.downStreamEmissionIntensity?.emissionDistance
          ?.previousMonthTotal ?? 0,
        2
      ),
      Target: truncateNumber(
        data?.downStreamEmissionIntensity?.emissionDistance?.percentageChange ??
          0,
        2
      ),
      // "Peer Average": 0,
    },
  ];

  const socailDiversityRows = [
    {
      Metric: "Gender diversity: Male Employees",
      "Current Month (%)": truncateNumber(
        data?.socialWorkforceDiversity?.maleEmployees?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month (%)": truncateNumber(
        data?.socialWorkforceDiversity?.maleEmployees?.previousMonthTotal ?? 0,
        2
      ),
      "% Change": truncateNumber(
        data?.socialWorkforceDiversity?.maleEmployees?.percentageChange ?? 0,
        2
      ),
      // "Peer Average (%)": Number(
      //   data?.socialWorkforceDiversity?.femaleEmployees?.peerAverage ?? 0
      // ),
    },
    {
      Metric: "Gender diversity: Female Employees",
      "Current Month (%)": Number(
        data?.socialWorkforceDiversity?.femaleEmployees?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month (%)": Number(
        data?.socialWorkforceDiversity?.femaleEmployees?.previousMonthTotal ??
          0,
        2
      ),
      "% Change": truncateNumber(
        data?.socialWorkforceDiversity?.maleEmployees?.percentageChange ?? 0,
        2
      ),
      // "Peer Average (%)": Number(
      //   data?.socialWorkforceDiversity?.lessThan30?.peerAverage ?? 0
      // ),
    },
    {
      Metric: "Gender diversity: Other Employees",
      "Current Month (%)": truncateNumber(
        data?.socialWorkforceDiversity?.otherEmployees?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month (%)": truncateNumber(
        data?.socialWorkforceDiversity?.otherEmployees?.previousMonthTotal ?? 0,
        2
      ),
      "% Change": truncateNumber(
        data?.socialWorkforceDiversity?.otherEmployees?.previousMonthTotal ?? 0,
        2
      ),
      // "Peer Average (%)": Number(
      //   data?.socialWorkforceDiversity?.between30And50?.peerAverage ?? 0
      // ),
    },
    {
      Metric: "Ethnicity diversity: Ethnic 1",
      "Current Month (%)": Number(
        data?.socialWorkforceDiversity?.greaterThan50?.thisMonthTotal ?? 0
      ),
      "Previous Month (%)": Number(
        data?.socialWorkforceDiversity?.greaterThan50?.previousMonthTotal ?? 0
      ),
      "% Change": "",
      // "Peer Average (%)": Number(
      //   data?.socialWorkforceDiversity?.greaterThan50?.peerAverage ?? 0
      // ),
    },
  ];

  const employeeEngagementRows = [
    {
      Metric: "Turnover Rate (%)",
      "Current Month": truncateNumber(
        data?.socialEmployeeEngagement?.turnoverRate?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month": truncateNumber(
        data?.socialEmployeeEngagement?.turnoverRate?.previousMonthTotal ?? 0,
        2
      ),
      "% Change": truncateNumber(
        data?.socialEmployeeEngagement?.turnoverRate?.percentageChange ?? 0,
        2
      ),
      // "Peer Average": truncateNumber(
      //   data?.socialEmployeeEngagement?.turnoverRate?.peerAverage ?? 0
      // ),
    },
    {
      Metric: "Total Training Hours",
      "Current Month": truncateNumber(
        data?.socialEmployeeEngagement?.trainingHours?.thisMonthTotal ?? 0,
        2
      ),
      "Previous Month": truncateNumber(
        data?.socialEmployeeEngagement?.trainingHours?.previousMonthTotal ?? 0,
        2
      ),
      "% Change": truncateNumber(
        data?.socialEmployeeEngagement?.trainingHours?.percentageChange ?? 0,
        2
      ),
      // "Peer Average": truncateNumber(
      //   data?.socialEmployeeEngagement?.trainingHours?.peerAverage ?? 0
      // ),
    },
  ];

  const healthSafetyRows = [
    {
      Metric:
        "Total Number of employee participated in health and safety training programs",
      "Current Month": Number(
        data?.socialhealthAndSafety?.employeeParticipationPercentage
          ?.thisMonthTotal ?? 0
      ),
      "Previous Month": Number(
        data?.socialhealthAndSafety?.employeeParticipationPercentage
          ?.previousMonthTotal ?? 0
      ),
      "% Change": Number(
        data?.socialhealthAndSafety?.employeeParticipationPercentage
          ?.percentageChange ?? 0
      ),
      // "Peer Average": Number(
      //   data?.socialhealthAndSafety?.employeeParticipationPercentage?.peerAverage ?? 0
      // ),
    },
    {
      Metric: "Total number of Safety Training Sessions",
      "Current Month":
        data?.socialhealthAndSafety?.trainingSessionsConducted
          ?.thisMonthTotal ?? 0,
      "Previous Month": Number(
        data?.socialhealthAndSafety?.trainingSessionsConducted
          ?.previousMonthTotal ?? 0
      ),
      "% Change": truncateNumber(
        data?.socialhealthAndSafety?.trainingSessionsConducted
          ?.percentageChange ?? 0,
        2
      ),
      // "Peer Average": Number(
      //   data?.socialhealthAndSafety?.safetyTrainingSessions?.peerAverage ?? 0
      // ),
    },
    {
      Metric: "Lost Time Injury Rate",
      "Current Month": Number(
        data?.socialhealthAndSafety?.lostTimeInjuryRate?.thisMonthTotal ?? 0
      ),
      "Previous Month": Number(
        data?.socialhealthAndSafety?.lostTimeInjuryRate?.previousMonthTotal ?? 0
      ),
      "% Change": truncateNumber(
        data?.socialhealthAndSafety?.lostTimeInjuryRate?.percentageChange ?? 0,
        2
      ),
      // "Peer Average": Number(
      //   data?.socialhealthAndSafety?.lostTimeInjuryRate?.peerAverage ?? 0
      // ),
    },
  ];

  const poshCasesRows = [
    {
      Metric: "POSH Cases Reported",
      "Current Month": Number(
        data?.socialPoshCases?.reported?.thisMonthTotal ?? 0
      ),
      "Previous Month": Number(
        data?.socialPoshCases?.reported?.previousMonthTotal ?? 0
      ),
      "% Change": "",
      // "Peer Average": Number(data?.socialPoshCases?.reported?.peerAverage ?? 0),
    },
    {
      Metric: "POSH Cases Resolved",
      "Current Month": Number(
        data?.socialPoshCases?.resolved?.thisMonthTotal ?? 0
      ),
      "Previous Month": Number(
        data?.socialPoshCases?.resolved?.previousMonthTotal ?? 0
      ),
      "% Change": "",
      // "Peer Average": Number(data?.socialPoshCases?.resolved?.peerAverage ?? 0),
    },
  ];

  const boardCompositionRows = [
    {
      Metric: "Total number Independent Directors",
      "Current Month": truncateNumber(
        data?.governmentBoardComposition?.independentDirectors
          ?.thisMonthTotal ?? 0
      ),
      "Baseline Year": truncateNumber(
        data?.governmentBoardComposition?.independentDirectors
          ?.previousMonthTotal ?? 0
      ),
      "% Change": truncateNumber(
        data?.governmentBoardComposition?.independentDirectors
          ?.percentageChange ?? 0
      ),
      // "Peer Average": truncateNumber(
      //   data?.governmentBoardComposition?.independentDirectors?.peerAverage ?? 0
      // ),
    },
    {
      Metric: "Total number board members",
      "Current Month": truncateNumber(
        data?.governmentBoardComposition?.totalBoardMembers?.thisMonthTotal ?? 0
      ),
      "Baseline Year": truncateNumber(
        data?.governmentBoardComposition?.totalBoardMembers
          ?.previousMonthTotal ?? 0
      ),
      "% Change": truncateNumber(
        data?.governmentBoardComposition?.totalBoardMembers?.percentageChange ??
          0
      ),
      // "Peer Average": truncateNumber(
      //   data?.governmentBoardComposition?.independentDirectors?.peerAverage ?? 0
      // ),
    },
    {
      Metric: "Total number female board members",
      "Current Month": truncateNumber(
        data?.governmentBoardComposition?.femaleBoardMembers?.thisMonthTotal ??
          0
      ),
      "Baseline Year": truncateNumber(
        data?.governmentBoardComposition?.femaleBoardMembers
          ?.previousMonthTotal ?? 0
      ),
      "% Change": truncateNumber(
        data?.governmentBoardComposition?.femaleBoardMembers
          ?.percentageChange ?? 0
      ),
      // "Peer Average": truncateNumber(
      //   data?.governmentBoardComposition?.meetingAttendanceRate?.peerAverage ??
      //     0
      // ),
    },
    {
      Metric: "Total number board members from minority groups",
      "Current Month": truncateNumber(
        data?.governmentBoardComposition?.minorityGroupBoardMembers
          ?.thisMonthTotal ?? 0
      ),
      "Baseline Year": truncateNumber(
        data?.governmentBoardComposition?.minorityGroupBoardMembers
          ?.previousMonthTotal ?? 0
      ),
      "% Change": truncateNumber(
        data?.governmentBoardComposition?.minorityGroupBoardMembers
          ?.percentageChange ?? 0
      ),
      // "Peer Average": truncateNumber(
      //   data?.governmentBoardComposition?.meetingAttendanceRate?.peerAverage ??
      //     0
      // ),
    },
  ];

  const ethicalPracticesRows = [
    {
      Metric: "Percentage of employees trained on ethics ",
      "Current Month": Number(
        data?.ethicalPractices?.employeeTrainedOnEthics?.thisMonthTotal ?? 0
      ),
      "Baseline Year": Number(
        data?.ethicalPractices?.employeeTrainedOnEthics?.previousMonthTotal ?? 0
      ),
      "% Change": Number(
        data?.ethicalPractices?.employeeTrainedOnEthics?.percentageChange ?? 0
      ),
      // "Peer Average": Number(
      //   data?.ethicalPractices?.ethicsTraning?.peerAverage ?? 0
      // ),
    },
    {
      Metric: "Reported Ethical Violations",
      "Current Month": Number(
        data?.ethicalPractices?.reportedEthicalViolations?.thisMonthTotal ?? 0
      ),
      "Baseline Year": Number(
        data?.ethicalPractices?.reportedEthicalViolations?.previousMonthTotal ??
          0
      ),
      "% Change": Number(
        data?.ethicalPractices?.reportedEthicalViolations?.percentageChange ?? 0
      ),
      // "Peer Average": Number(
      //   data?.ethicalPractices?.reportedEthicalViolations
      //     ?.peerAverage ?? 0
      // ),
    },
  ];

  const cyberSecurityRows = [
    {
      Metric: "Number of Compliance Violations",
      "Current Quarter": Number(
        data?.complianceAndEthics?.complianceViolations?.thisMonthTotal ?? 0
      ),
      "Previous Quarter": Number(
        data?.complianceAndEthics?.complianceViolations?.previousMonthTotal ?? 0
      ),
      "% Change": Number(
        data?.complianceAndEthics?.complianceViolations?.percentageChange ?? 0
      ),
      // "Peer Average": Number(
      //   data?.cybersecurityIncidents?.reported?.peerAverage ?? 0
      // ),
    },
    {
      Metric: "Voilations",
      "Current Quarter": Number(
        data?.complianceAndEthics?.ethicalViolations?.thisMonthTotal ?? 0
      ),
      "Previous Quarter": Number(
        data?.complianceAndEthics?.ethicalViolations?.previousMonthTotal ?? 0
      ),
      "% Change": Number(
        data?.complianceAndEthics?.ethicalViolations?.percentageChange ?? 0
      ),
      // "Peer Average": Number(
      //   data?.cybersecurityIncidents?.dataBreaches?.peerAverage ?? 0
      // ),
    },
    {
      Metric: "Total Fines and Penalties Paid",
      "Current Quarter": Number(
        data?.complianceAndEthics?.totalFinesAndPenalties?.thisMonthTotal ?? 0
      ),
      "Previous Quarter": Number(
        data?.complianceAndEthics?.totalFinesAndPenalties?.previousMonthTotal ??
          0
      ),
      "% Change": Number(
        data?.complianceAndEthics?.totalFinesAndPenalties?.percentageChange ?? 0
      ),
      // "Peer Average": Number(
      //   data?.cybersecurityIncidents?.successfulPhishingAttempts?.peerAverage ??
      //     0
      // ),
    },
    {
      Metric: "Percentage of employees trained on Ethics",
      "Current Quarter": Number(
        data?.complianceAndEthics?.employeesTrainedPercentage?.thisMonthTotal ??
          0
      ),
      "Previous Quarter": Number(
        data?.complianceAndEthics?.employeesTrainedPercentage
          ?.previousMonthTotal ?? 0
      ),
      "% Change": Number(
        data?.complianceAndEthics?.employeesTrainedPercentage
          ?.percentageChange ?? 0
      ),
      // "Peer Average": Number(
      //   data?.cybersecurityIncidents?.successfulPhishingAttempts?.peerAverage ??
      //     0
      // ),
    },
  ];

  let barCharDataSet = [
    {
      label: "Independent Directors",
      data: [65, 50],
      backgroundColor: "rgba(255, 79, 79, 0.8)",
      borderColor: "rgba(255, 79, 79, 1)",
      borderWidth: 1,
    },
    {
      label: "Meeting Attendance Rate",
      data: [85, 70],
      backgroundColor: "rgba(14, 98, 224, 0.8)",
      borderColor: "rgba(14, 98, 224, 1)",
      borderWidth: 1,
    },
  ];

  let scope1Data = [
    {
      label: "Current Month",
      data: scope1BarChartData?.map((item) => item.thisMonthEmission) ?? [],
      backgroundColor: "rgba(255, 79, 79, 0.8)",
      borderColor: "rgba(255, 79, 79, 1)",
      borderWidth: 1,
    },
    {
      label: "Previous Month",
      data: scope1BarChartData?.map((item) => item.previousMonthEmission) ?? [],
      backgroundColor: "rgba(14, 98, 224, 0.8)",
      borderColor: "rgba(14, 98, 224, 1)",
      borderWidth: 1,
    },
  ];

  let scope2Data = [
    {
      label: "Current Month",
      data: scope2BarChartData?.map((item) => item.thisMonthEmission) ?? [],
      backgroundColor: "rgba(255, 79, 79, 0.8)",
      borderColor: "rgba(255, 79, 79, 1)",
      borderWidth: 1,
    },
    {
      label: "Previous Month",
      data: scope2BarChartData?.map((item) => item.previousMonthEmission) ?? [],
      backgroundColor: "rgba(14, 98, 224, 0.8)",
      borderColor: "rgba(14, 98, 224, 1)",
      borderWidth: 1,
    },
  ];

  let scope3Data = [
    {
      label: "Current Month",
      data: scope3BarChartData?.map((item) => item.thisMonthEmission) ?? [],
      backgroundColor: "rgba(255, 79, 79, 0.8)",
      borderColor: "rgba(255, 79, 79, 1)",
      borderWidth: 1,
    },
    {
      label: "Previous Month",
      data: scope3BarChartData?.map((item) => item.previousMonthEmission) ?? [],
      backgroundColor: "rgba(14, 98, 224, 0.8)",
      borderColor: "rgba(14, 98, 224, 1)",
      borderWidth: 1,
    },
  ];

  let waterDischargeData = [
    {
      label: "Current Month",
      data:
        data?.misReportFacilityWise?.waterFacilitySummary?.map(
          (item) => item?.thisMonth?.totalWaterDischarged
        ) ?? [],
      backgroundColor: "rgba(255, 79, 79, 0.8)",
      borderColor: "rgba(255, 79, 79, 1)",
      borderWidth: 1,
    },
    {
      label: "Previous Month",
      data:
        data?.misReportFacilityWise?.waterFacilitySummary?.map(
          (item) => item.previousMonth?.totalWaterDischarged
        ) ?? [],
      backgroundColor: "rgba(14, 98, 224, 0.8)",
      borderColor: "rgba(14, 98, 224, 1)",
      borderWidth: 1,
    },
  ];

  let waterWithdrawalData = [
    {
      label: "Current Month",
      data:
        data?.misReportFacilityWise?.waterFacilitySummary?.map(
          (item) => item?.thisMonth?.totalWaterWithdrawal
        ) ?? [],
      backgroundColor: "rgba(255, 79, 79, 0.8)",
      borderColor: "rgba(255, 79, 79, 1)",
      borderWidth: 1,
    },
    {
      label: "Previous Month",
      data:
        data?.misReportFacilityWise?.waterFacilitySummary?.map(
          (item) => item.previousMonth?.totalWaterWithdrawal
        ) ?? [],
      backgroundColor: "rgba(14, 98, 224, 0.8)",
      borderColor: "rgba(14, 98, 224, 1)",
      borderWidth: 1,
    },
  ];

  let waterTreatedData = [
    {
      label: "Current Month",
      data:
        data?.misReportFacilityWise?.waterFacilitySummary?.map(
          (item) => item?.thisMonth?.totalWastewaterTreated
        ) ?? [],
      backgroundColor: "rgba(255, 79, 79, 0.8)",
      borderColor: "rgba(255, 79, 79, 1)",
      borderWidth: 1,
    },
    {
      label: "Previous Month",
      data:
        data?.misReportFacilityWise?.waterFacilitySummary?.map(
          (item) => item.previousMonth?.totalWastewaterTreated
        ) ?? [],
      backgroundColor: "rgba(14, 98, 224, 0.8)",
      borderColor: "rgba(14, 98, 224, 1)",
      borderWidth: 1,
    },
  ];

  let waterCircularityData = [
    {
      label: "Current Month",
      data:
        data?.misReportFacilityWise?.waterFacilitySummary?.map(
          (item) => item?.thisMonth?.totalWaterRecycled
        ) ?? [],
      backgroundColor: "rgba(255, 79, 79, 0.8)",
      borderColor: "rgba(255, 79, 79, 1)",
      borderWidth: 1,
    },
    {
      label: "Previous Month",
      data:
        data?.misReportFacilityWise?.waterFacilitySummary?.map(
          (item) => item.previousMonth?.totalWaterRecycled
        ) ?? [],
      backgroundColor: "rgba(14, 98, 224, 0.8)",
      borderColor: "rgba(14, 98, 224, 1)",
      borderWidth: 1,
    },
  ];

  const open = Boolean(anchorEl);
  const downloadReport = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const waitForImagesToLoad = (element) => {
    const images = element.querySelectorAll("img");
    const promises = Array.from(images).map((img) => {
      if (img.complete) return Promise.resolve();
      return new Promise((resolve) => {
        img.onload = img.onerror = resolve;
      });
    });
    return Promise.all(promises);
  };

  // const downloadReportPdf = async () => {
  //   handleClose();

  //   const input = reportRef.current;
  //   if (!input) {
  //     console.error("❌ reportRef is null");
  //     return;
  //   }

  //   await waitForImagesToLoad(input);

  //   const canvas = await html2canvas(input, { scale: 2, useCORS: true });
  //   const fullImageHeight = canvas.height;
  //   const fullImageWidth = canvas.width;

  //   const pageBreakEls = input.querySelectorAll(`.${styles.pageBreak}`);
  //   const pageBreakOffsets = Array.from(pageBreakEls).map((el) => {
  //     const rect = el.getBoundingClientRect();
  //     const parentRect = input.getBoundingClientRect();
  //     return Math.round(
  //       (rect.top - parentRect.top) * (canvas.height / input.clientHeight)
  //     );
  //   });

  //   const pdf = new jsPDF("p", "mm", "a4");
  //   const margin = 10;
  //   const pageWidth = 210;
  //   const pageHeight = 297;

  //   const pdfContentWidth = pageWidth - 2 * margin;
  //   const pdfContentHeight = pageHeight - 2 * margin;

  //   const slices = [];
  //   let startY = 0;

  //   // Add last canvas height if needed
  //   if (
  //     pageBreakOffsets.length === 0 ||
  //     pageBreakOffsets[pageBreakOffsets.length - 1] < fullImageHeight
  //   ) {
  //     pageBreakOffsets.push(fullImageHeight);
  //   }

  //   pageBreakOffsets.forEach((offsetY) => {
  //     const sliceHeight = offsetY - startY;

  //     const sliceCanvas = document.createElement("canvas");
  //     sliceCanvas.width = fullImageWidth;
  //     sliceCanvas.height = sliceHeight;

  //     const ctx = sliceCanvas.getContext("2d");
  //     ctx.drawImage(
  //       canvas,
  //       0,
  //       startY,
  //       fullImageWidth,
  //       sliceHeight,
  //       0,
  //       0,
  //       fullImageWidth,
  //       sliceHeight
  //     );

  //     const imgData = sliceCanvas.toDataURL("image/png");

  //     const scaledHeight = (sliceHeight * pdfContentWidth) / fullImageWidth;

  //     if (slices.length > 0) pdf.addPage();
  //     pdf.addImage(
  //       imgData,
  //       "PNG",
  //       margin,
  //       margin,
  //       pdfContentWidth,
  //       scaledHeight
  //     );

  //     slices.push(imgData);
  //     startY = offsetY;
  //   });

  //   pdf.save("MIS_Report_With_PageBreaks.pdf");
  // };

  const downloadReportPdf = () => {
    handleClose();

    const element = reportRef.current;
    if (!element) {
      console.error("reportRef is null");
      return;
    }

    element.classList.add("pdf-mode");

    const opt = {
      margin: [10, 7.5, 10, 7.5],
      filename: "MIS_Report.pdf",
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 3, useCORS: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      pagebreak: { mode: ["css", "legacy"] },
    };

    html2pdf()
      .set(opt)
      .from(element)
      .toPdf()
      .get("pdf")
      .then((pdf) => {
        const totalPages = pdf.internal.getNumberOfPages();
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);
          pdf.setFontSize(10);
          pdf.text(
            `Page ${i} of ${totalPages}`,
            pageWidth - 40, // Adjust for right alignment
            pageHeight - 10 // Bottom position
          );
        }

        pdf.save("MIS_Report.pdf");
      })
      .finally(() => {
        element.classList.remove("pdf-mode");
      });
  };

  const downloadReportWord = () => {
    handleClose();
    const input = reportRef.current;
    if (!input) {
      console.error("❌ reportRef is null");
      return;
    }

    const buildWordHtml = () => {
      const clone = input.cloneNode(true);
      const sourceCanvases = input.querySelectorAll("canvas");
      const cloneCanvasList = [...clone.querySelectorAll("canvas")];

      sourceCanvases.forEach((sourceCanvas, index) => {
        const target = cloneCanvasList[index];
        if (!target || !(sourceCanvas instanceof HTMLCanvasElement)) return;
        const exported = chartCanvasForWordExport(sourceCanvas, 640);
        if (!exported) return;
        try {
          const img = document.createElement("img");
          img.src = exported.dataUrl;
          img.alt = "Chart";
          img.setAttribute("width", String(exported.width));
          img.setAttribute("height", String(exported.height));
          target.replaceWith(img);
        } catch (err) {
          console.warn("Could not export chart canvas to Word:", err);
        }
      });

      let htmlContent = clone.innerHTML;

    const pageBreakHTML = "<br style='page-break-before: always;'>";

    // Add page break before large tables (scrollHeight > 800)
    const tables = input.querySelectorAll("table");
    tables.forEach((table) => {
      if (table.scrollHeight > 800) {
        htmlContent = htmlContent.replace(
          table.outerHTML,
          pageBreakHTML + table.outerHTML
        );
      }
    });

    // Add page break before scope headings
    htmlContent = htmlContent.replace(
      /(<h3>Scope \d+:.*?<\/h3>)/g,
      `${pageBreakHTML}$1`
    );

    const header = `
      <html xmlns:o='urn:schemas-microsoft-com:office:office'
            xmlns:w='urn:schemas-microsoft-com:office:word'
            xmlns='http://www.w3.org/TR/REC-html40'>
        <head>
          <meta charset='utf-8'>
          <title>Document</title>
          <style>
            table {
              font-size: 12px;
              border-collapse: collapse;
              width: 100%;
              page-break-inside: avoid; /* Avoid splitting table */
            }
            thead {
              display: table-header-group;
            }
            tfoot {
              display: table-footer-group;
            }
            tr {
              page-break-inside: avoid;
              page-break-after: auto;
            }
            td, th {
              border: 1px solid #000;
              padding: 4px;
              text-align: left;
            }
          </style>
        </head>
        <body>
    `;

    const footer = "</body></html>";

    const sourceHTML = header + htmlContent + footer;

    const blob = new Blob(["\ufeff", sourceHTML], {
      type: "application/msword",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "MIS_Report.doc";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    };

    // Chart.js renders to canvas; innerHTML omits pixels. Wait a frame so charts are painted, then rasterize canvases to PNG for Word.
    requestAnimationFrame(() => {
      requestAnimationFrame(buildWordHtml);
    });
  };
  
  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "1.5rem",
          padding: "0 1rem",
        }}
      >
        <p
          style={{
            fontFamily: "Inter",
            fontSize: "22px",
            fontWeight: "700",
          }}
        >
          Monthly MIS Report
        </p>
        <Button
          startIcon={<DownloadIcon />}
          variant="contained"
          sx={{
            backgroundColor: "#1C486B",
            padding: "0.5rem 1rem",
            borderRadius: "4px",
            height: "40px",
            textTransform: "none",
          }}
          onClick={downloadReport}
        >
          Download Report
        </Button>
        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
          <MenuItem onClick={downloadReportPdf}>Download as PDF</MenuItem>
          <MenuItem onClick={downloadReportWord}>Download as Word</MenuItem>
        </Menu>
      </div>

      <div ref={reportRef} style={{ padding: "1rem" }}>
        {base64Logo ? (
          <img src={base64Logo} alt="Company Logo" className={styles.logo} />
        ) : null}

        <table className={styles.table}>
          <tr>
            <td className={styles.table_heading}>Company Name :</td>
            <td className={styles.table_value}>{user?.company?.companyName}</td>
          </tr>
          {/* <tr>
            <td className={styles.table_heading}>User Name :</td>
            <td className={styles.table_value}>{user?.company?.admin?.name}</td>
          </tr> */}
          <tr>
            <td className={styles.table_heading}>Reporting Month :</td>
            <td className={styles.table_value}>
              {data?.reportingMonth ?? "-"}
            </td>
          </tr>
          {/* <tr>
            <td className={styles.table_heading}>Prepared By :</td>
            <td className={styles.table_value}>
              Auto Generated through Platform
            </td>
          </tr> */}
          <tr>
            <td className={styles.table_heading}>Date :</td>
            <td className={styles.table_value}>
              {new Date(data?.updatedAt).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
            </td>
          </tr>
        </table>

        <div style={{ marginTop: "2rem" }}>
          <p className={styles.heading}>1. Executive Summary</p>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              marginLeft: "1rem",
            }}
          >
            <p className={styles.summary_heading}>Overall ESG Performance</p>
            <p className={styles.summary_subheading}>
              {data?.executiveSummary?.overallPerformance}
            </p>
            <p className={styles.summary_heading}>
              Performance Compared to Previous Month
            </p>
            <p className={styles.summary_subheading}>
              {data?.executiveSummary?.baselineComparison}
            </p>
            {/* <p className={styles.summary_heading}>
              Performance Compared to Peer Companies
            </p>
            <p className={styles.summary_subheading}>
              {data?.executiveSummary?.peerComparison}
            </p> */}
          </div>
        </div>

        <div className={styles.pageBreak} />
        <div style={{ marginTop: "2rem" }}>
          <p className={styles.heading}>2. Environmental Metrics</p>
          <p className={styles.sub_heading}>2.1 GHG Emissions</p>
          <div className={styles.direct_emission_div}>
            <p className={styles.sub_heading}>Scope 1: Direct Emissions</p>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              {/* <BarChart
                labels={["Current Month", "Baseline Year"]}
                datasets={barCharDataSet}
                customWidth={250}
                customHeight={250}
              /> */}
            </div>

            <CustomTable
              headers={scope1Headers}
              rows={scope1Rows}
              month={data?.month}
            />
          </div>

          <div className={styles.direct_emission_div}>
            <p className={styles.sub_heading}>
              Scope 2: Indirect Emissions from Purchased Energy
            </p>
            <CustomTable
              headers={scope1Headers}
              rows={scope2Rows}
              month={data?.month}
            />
          </div>

          <div className={styles.direct_emission_div}>
            <p className={styles.sub_heading}>
              Scope 3: Emission from Value Chain
            </p>
            <CustomTable
              headers={scope1Headers}
              rows={scope3Rows}
              month={data?.month}
            />
          </div>

          <div className={styles.pageBreak} />
          <div className={styles.direct_emission_div}>
            <p className={styles.sub_heading}>2.2 Energy Consumption</p>
            <CustomTable
              headers={energyConsumptionHeaders}
              rows={energyConsumptionRows}
              month={data?.month}
            />
          </div>

          <div className={styles.direct_emission_div}>
            <p className={styles.sub_heading}>2.3 Water Consumption</p>
            <CustomTable
              headers={waterConsumptionHeaders}
              rows={waterConsumptionRows}
              month={data?.month}
            />
          </div>

          <div className={styles.direct_emission_div}>
            <p className={styles.sub_heading}>2.4 Waste Management</p>
            <CustomTable
              headers={wasteManagementHeaders}
              rows={wasteManagementRows}
              month={data?.month}
            />
          </div>

          <div className={styles.pageBreak} />
          <div className={styles.direct_emission_div}>
            <p className={styles.sub_heading}>
              2.5 Upstream Transportation and Distribution
            </p>
            <p style={{ marginLeft: "2.5rem", marginTop: "-1rem" }}>
              Logistics Summary
            </p>
            <CustomTable
              headers={logisticSummaryHeaders}
              rows={upstreamLogisticRows}
              month={data?.month}
            />

            <p style={{ marginLeft: "2.5rem", marginTop: "1rem" }}>
              Emissions Intensity
            </p>
            <CustomTable
              headers={emissionIntensityHeaders}
              rows={upstreamEmissionIntensityRows}
              month={data?.month}
            />
          </div>

          <div className={styles.direct_emission_div}>
            <p className={styles.sub_heading}>
              2.6 Downstream Transportation and Distribution
            </p>
            <p style={{ marginLeft: "2.5rem", marginTop: "-1rem" }}>
              Logistics Summary
            </p>
            <CustomTable
              headers={logisticSummaryHeaders}
              rows={downstreamLogisticRows}
              month={data?.month}
            />

            <p style={{ marginLeft: "2.5rem", marginTop: "1rem" }}>
              Emissions Intensity
            </p>
            <CustomTable
              headers={emissionIntensityHeaders}
              rows={downstreamEmissionIntensityRows}
              month={data?.month}
            />
          </div>
        </div>

        <div className={styles.pageBreak} />
        {data?.typeOfReport === "Consolidated" ? (
          <>
            <div style={{ marginTop: "2rem" }}>
              <p className={styles.heading}>3. Social Metrics</p>

              <div className={styles.direct_emission_div}>
                <p className={styles.sub_heading}>3.1 Workforce Diversity</p>
                <CustomTable
                  headers={socialMetricsDiversityHeaders}
                  rows={socailDiversityRows}
                  month={data?.month}
                />
              </div>
              <div className={styles.direct_emission_div}>
                <p className={styles.sub_heading}>3.2 Employee Engagement</p>
                <CustomTable
                  headers={socialMetricsHeaders}
                  rows={employeeEngagementRows}
                  month={data?.month}
                />
              </div>

              <div className={styles.direct_emission_div}>
                <p className={styles.sub_heading}>3.3 Health and Safety</p>
                <CustomTable
                  headers={socialMetricsHeaders}
                  rows={healthSafetyRows}
                  month={data?.month}
                />
              </div>
              {/* <div style={{ marginTop: "1rem" }}>
            <p className={styles.sub_heading}>3.4 POSH Cases</p>
            <CustomTable headers={socialMetricsHeaders} rows={poshCasesRows} />
          </div> */}
            </div>

            <div className={styles.pageBreak} />
            <div style={{ marginTop: "2rem" }}>
              <p className={styles.heading}>4. Governance Metrics</p>

              <div className={styles.direct_emission_div}>
                <p className={styles.sub_heading_graph}>
                  4.1 Board Composition
                </p>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  {/* <BarChart
                labels={["Current Month", "Baseline Year"]}
                datasets={barCharDataSet}
                customWidth={250}
                customHeight={250}
              /> */}
                </div>
                <CustomTable
                  headers={govtMetricsHeaders}
                  rows={boardCompositionRows}
                  month={data?.month}
                />
              </div>

              <div className={styles.direct_emission_div}>
                <p className={styles.sub_heading_graph}>
                  4.2 Ethical Practices
                </p>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  {/* <BarChart
                labels={["Current Month", "Baseline Year"]}
                datasets={barCharDataSet}
                customWidth={250}
                customHeight={250}
              /> */}
                </div>
                <CustomTable
                  headers={govtMetricsHeaders}
                  rows={ethicalPracticesRows}
                  month={data?.month}
                />
              </div>

              <div className={styles.direct_emission_div}>
                <p className={styles.sub_heading_graph}>
                  4.3 Compliance and Ethics
                </p>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  {/* <BarChart
                labels={["Current Month", "Baseline Year"]}
                datasets={barCharDataSet}
                customWidth={250}
                customHeight={250}
              /> */}
                </div>
                <CustomTable
                  headers={govtMetricsHeaders}
                  rows={cyberSecurityRows}
                  month={data?.month}
                />
              </div>

              {/* <div className={styles.direct_emission_div}>
            <div className={styles.vehicle_div}>
              <div>
                <p className={styles.sub_heading_graph}>
                  Vehicles Used by Employees
                </p>
                <p>60,000</p>
              </div>
              <div>
                <p>Vehcile Type</p>
              </div>
            </div>
          </div> */}
            </div>
          </>
        ) : null}

        <div className={styles.pageBreak} />
        {data?.typeOfReport === "Consolidated" ? (
          <div style={{ marginTop: "2rem" }}>
            <p className={styles.heading}>5. Facility-wise Analytics</p>

            <div className={styles.direct_emission_div}>
              <p className={styles.sub_heading_graph}>5.1 GHG Accounting:</p>
              <div>
                <p className={styles.sub_sub_heading}>Scope 1</p>
                <BarChart
                  labels={
                    scope1BarChartData &&
                    scope1BarChartData?.map((item) => item?.facility)
                  }
                  datasets={scope1Data}
                  display={true}
                />
                <p className={styles.summary_text}>
                  {data?.scope1Summary ?? summary?.scope1Summary}
                </p>
              </div>

              <div>
                <p className={styles.sub_sub_heading}>Scope 2</p>
                <BarChart
                  labels={
                    scope2BarChartData &&
                    scope2BarChartData?.map((item) => item?.facility)
                  }
                  datasets={scope2Data}
                  display={true}
                />
                <p className={styles.summary_text}>
                  {data?.scope2Summary ?? summary?.scope2Summary}
                </p>
              </div>

              <div className={styles.pageBreak} />
              <div>
                <p className={styles.sub_sub_heading}>Scope 3</p>
                <BarChart
                  labels={
                    scope3BarChartData &&
                    scope3BarChartData?.map((item) => item?.facility)
                  }
                  datasets={scope3Data}
                  display={true}
                />
                <p className={styles.summary_text}>
                  {data?.scope3Summary ?? summary?.scope3Summary}
                </p>
              </div>
            </div>

            <div className={styles.direct_emission_div}>
              <p className={styles.sub_heading_graph}>5.2 Water:</p>
              <div>
                <p className={styles.sub_sub_heading}>Water Withdrawal</p>
                <BarChart
                  labels={
                    data?.misReportFacilityWise?.waterFacilitySummary?.map(
                      (item) => item?.facility
                    ) ?? []
                  }
                  datasets={waterWithdrawalData}
                />
                <p className={styles.summary_text}>
                  {data?.waterSummary?.waterWithdrawal ?? "-"}
                </p>
              </div>

              <div className={styles.pageBreak} />
              <div>
                <p className={styles.sub_sub_heading}>Waste Water Discharged</p>
                <BarChart
                  labels={
                    data?.misReportFacilityWise?.waterFacilitySummary?.map(
                      (item) => item?.facility
                    ) ?? []
                  }
                  datasets={waterDischargeData}
                />
                <p className={styles.summary_text}>
                  {data?.waterSummary?.waterDischarged ?? "-"}
                </p>
              </div>

              <div>
                <p className={styles.sub_sub_heading}>Waste Water Treated</p>
                <BarChart
                  labels={
                    data?.misReportFacilityWise?.waterFacilitySummary?.map(
                      (item) => item?.facility
                    ) ?? []
                  }
                  datasets={waterTreatedData}
                />
                <p className={styles.summary_text}>
                  {data?.waterSummary?.waterTreated ?? "-"}
                </p>
              </div>

              <div className={styles.pageBreak} />
              <div>
                <p className={styles.sub_sub_heading}>Water Circularity</p>
                <BarChart
                  labels={
                    data?.misReportFacilityWise?.waterFacilitySummary?.map(
                      (item) => item?.facility
                    ) ?? []
                  }
                  datasets={waterCircularityData}
                />
                <p className={styles.summary_text}>
                  {data?.waterSummary?.waterCircularity ?? "-"}
                </p>
              </div>
            </div>
          </div>
        ) : null}

        {/* <div style={{ marginTop: "2rem" }}>
          <p className={styles.heading}>6. Conclusion</p>

          <div style={{ marginLeft: "1rem" }}>
            <p className={styles.sub_heading_graph}>6.1 BRSR</p>
            <p className={styles.sub_sub_heading}>
              Reporting Status (% completed)
            </p>

            <p className={styles.sub_heading_graph}>6.2 GRI</p>
            <p className={styles.sub_sub_heading}>
              Reporting Status (% completed)
            </p>

            <p className={styles.sub_heading_graph}>6.3 CDP</p>
            <p className={styles.sub_sub_heading}>
              Reporting Status (% completed)
            </p>
          </div>
        </div> */}

        <div className={styles.pageBreak} />
        <div style={{ marginTop: "2rem" }}>
          <p className={styles.heading}>
            {data?.typeOfReport === "Consolidated" ? 6 : 3}. Conclusion
          </p>

          <div style={{ marginLeft: "1rem" }}>
            <p className={styles.sub_sub_heading}>Summary of key findings</p>
            <p className={styles.summary_subheading}>
              {data?.executiveSummary?.summaryOfFindings}
            </p>
            <p className={styles.sub_sub_heading}>
              Recommendations for the upcoming month
            </p>
            <p className={styles.summary_subheading}>
              {data?.executiveSummary?.recommendations}
            </p>
          </div>
        </div>

        <div style={{ marginTop: "3rem" }}>
          <p className={styles.heading}>Appendices</p>

          <div style={{ marginLeft: "1rem" }}>
            <p className={styles.sub_main_heading} style={{ fontStyle: "normal" }}>
              <span className={styles.appendix_label}>Appendix A: Emission Source</span>
            </p>  <ol className={styles.appendix_text} style={{ marginTop: "0.25rem" }}>
              {(data?.sources ?? []).map((source, index) => (
                <li key={`${source}-${index}`}>{source}</li>
              ))}
            </ol>
          
            {/* <p className={styles.sub_main_heading} style={{ fontStyle: "normal" }}>
              <span className={styles.appendix_label}>Appendix B:</span>{" "}
              <span className={styles.appendix_text}>
                {dataSource?.appendixB || "Detailed Energy Consumption Data"}
              </span>
            </p>
            <p className={styles.sub_main_heading} style={{ fontStyle: "normal" }}>
              <span className={styles.appendix_label}>Appendix C:</span>{" "}
              <span className={styles.appendix_text}>
                {dataSource?.appendixC || "Detailed Workforce Data"}
              </span>
            </p> */}
            {/* <p className={styles.sub_main_heading} style={{ fontStyle: "normal" }}>
              <span className={styles.appendix_label}>Appendix D:</span>{" "}
              <span className={styles.appendix_text}>
                {dataSource?.appendixD || "Detailed Board and Governance Data"}
              </span>
            </p> */}
          </div>
          {/* <p className={styles.sub_main_heading}>
            Note: All values will be auto-filled and validated by the platform
            using AI to ensure accuracy and consistency in reporting.{" "}
          </p> */}
        </div>

        <div className={styles.company_footer}>
          <img src={companyLogo} alt="logo" width={150} />
          <p className={styles.sub_sub_heading}>Report Powered by Nirantara</p>
        </div>
      </div>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
        open={loading}
      >
        <CircularProgress color="inherit" />
        <Typography sx={{ mt: 2 }}>
          We are comparing MIS Report. Please wait...
        </Typography>
      </Backdrop>
    </>
  );
};

export default MisReportCard;
